import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'components/ui/alert-dialog';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useDeleteToolApiAction } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import { useToast } from 'hooks/use-toast';

export function ToolApiDeleteActionModal({
  toolId,
  actionId,
  open,
  onOpenChange,
  projectKey,
  isBlocksTool,
}: {
  toolId: string;
  actionId: string;
  open: boolean;
  onOpenChange: (value: boolean) => void;
  projectKey: string;
  isBlocksTool?: boolean;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useDeleteToolApiAction();

  const handleConfirm = async () => {
    try {
      const response = await mutateAsync({ toolId, actionId, projectKey, isBlocksTool });
      if (!response.is_success) {
        throw new Error(
          response.message || response.detail || t('FAILED_TO_DELETE_ACTION') || 'Failed to delete action'
        );
      }
      toast({
        variant: 'success',
        title: t('ACTION_DELETED') || 'Action deleted',
        description:
          response.detail || t('ACTION_DELETED_SUCCESSFULLY') || 'Action deleted successfully.',
      });
      queryClient.invalidateQueries({ queryKey: ['tools', 'detail', toolId] });
      onOpenChange(false);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('DELETE_FAILED') || 'Delete failed',
        description:
          error instanceof Error
            ? error.message
            : t('FAILED_TO_DELETE_ACTION') || 'Failed to delete action.',
      });
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('DELETE_ACTION') || 'Delete Action'}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('DELETE_ACTION_CONFIRMATION') || 'Are you sure you want to delete this API action?'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>{t('CANCEL') || 'Cancel'}</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm} disabled={isPending}>
            {isPending ? t('DELETING') || 'Deleting...' : t('DELETE') || 'Delete'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
